/**
 * TradeStack - Estimate Form Page (Create/Edit)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import '../styles/Forms.css';

function EstimateForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { formatCurrency } = useConfig();

  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingEstimate, setLoadingEstimate] = useState(isEditMode);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const customerId = searchParams.get('customerId');
  const jobId = searchParams.get('jobId');

  const [formData, setFormData] = useState({
    customer_id: customerId || '',
    job_id: jobId || '',
    valid_days: 30,
    notes: ''
  });

  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: '', amount: 0 }
  ]);

  useEffect(() => {
    loadData();
    if (isEditMode) {
      loadEstimate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      const [customersRes, jobsRes] = await Promise.all([
        api.getCustomers({ limit: 1000 }),
        api.getJobs({ limit: 1000 })
      ]);
      setCustomers(customersRes.data.customers || []);
      setJobs(jobsRes.data.jobs || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const loadEstimate = async () => {
    try {
      const response = await api.getEstimate(id);
      const estimate = response.data.estimate;
      setFormData({
        customer_id: estimate.customer_id || '',
        job_id: estimate.job_id || '',
        valid_days: estimate.valid_days || 30,
        notes: estimate.notes || ''
      });
      if (estimate.line_items && estimate.line_items.length > 0) {
        setLineItems(estimate.line_items.map(item => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          unit_price: item.unit_price || '',
          amount: item.amount || 0
        })));
      }
    } catch (err) {
      setError('Failed to load estimate');
      console.error(err);
    } finally {
      setLoadingEstimate(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleLineItemChange = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;

    // Recalculate amount
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].unit_price) || 0;
      updated[index].amount = qty * price;
    }

    setLineItems(updated);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: '', amount: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        customer_id: formData.customer_id || null,
        job_id: formData.job_id || null,
        line_items: lineItems.filter(item => item.description.trim()),
        subtotal: calculateTotal(),
        total: calculateTotal()
      };

      if (isEditMode) {
        await api.updateEstimate(id, submitData);
        navigate(`/estimates/${id}`);
      } else {
        const response = await api.createEstimate(submitData);
        navigate(`/estimates/${response.data.estimate.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} estimate`);
    } finally {
      setLoading(false);
    }
  };

  if (loadingEstimate) {
    return <div className="form-page"><div className="loading-state">Loading...</div></div>;
  }

  return (
    <div className="form-page">
      <div className="detail-header">
        <div className="header-left">
          <Link to={isEditMode ? `/estimates/${id}` : '/estimates'} className="back-link">
            &larr; {isEditMode ? 'Back to Estimate' : 'Back to Estimates'}
          </Link>
          <h1>{isEditMode ? 'Edit Estimate' : 'New Estimate'}</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3>Estimate Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customer_id">Customer</label>
              <select
                id="customer_id"
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                disabled={loadingData}
              >
                <option value="">-- Select Customer --</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="job_id">Job (Optional)</label>
              <select
                id="job_id"
                name="job_id"
                value={formData.job_id}
                onChange={handleChange}
                disabled={loadingData}
              >
                <option value="">-- No Job --</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="valid_days">Valid For (days)</label>
            <input
              type="number"
              id="valid_days"
              name="valid_days"
              value={formData.valid_days}
              onChange={handleChange}
              min="1"
              max="365"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Line Items</h3>
          <div className="line-items-table">
            <div className="line-item-header">
              <span className="col-description">Description</span>
              <span className="col-quantity">Qty</span>
              <span className="col-price">Unit Price</span>
              <span className="col-amount">Amount</span>
              <span className="col-actions"></span>
            </div>
            {lineItems.map((item, index) => (
              <div key={index} className="line-item-row">
                <input
                  type="text"
                  className="col-description"
                  value={item.description}
                  onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                  placeholder="Item description"
                />
                <input
                  type="number"
                  className="col-quantity"
                  value={item.quantity}
                  onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                  min="1"
                />
                <input
                  type="number"
                  className="col-price"
                  value={item.unit_price}
                  onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
                <span className="col-amount">{formatCurrency(item.amount)}</span>
                <button
                  type="button"
                  className="col-actions btn-icon"
                  onClick={() => removeLineItem(index)}
                  disabled={lineItems.length === 1}
                >
                  &times;
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addLineItem}>
              + Add Line Item
            </button>
          </div>

          <div className="estimate-total">
            <span className="total-label">Total:</span>
            <span className="total-value">{formatCurrency(calculateTotal())}</span>
          </div>
        </div>

        <div className="form-section">
          <h3>Notes</h3>
          <div className="form-group">
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Terms, conditions, or additional notes..."
            />
          </div>
        </div>

        <div className="form-actions">
          <Link to={isEditMode ? `/estimates/${id}` : '/estimates'} className="btn btn-secondary">Cancel</Link>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Estimate')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EstimateForm;
